import { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import clsx from 'clsx';
import type { Driver } from '../../types/driver';
import { RISK_LABELS, RISK_COLORS } from '../../utils/constants';
import { formatScore } from '../../utils/formatters';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface DriverScoreBadgeProps {
  score: Driver['score'];
  size?: 'sm' | 'md' | 'lg';
}

// `score` sigue siendo opcional (FE-16): un conductor puede no salir en la
// respuesta de `/rankings`/`/me/score` (fuera de alcance) o la llamada puede
// fallar. Se muestra "Pendiente" en vez de inventar un valor.
export function DriverScoreBadge({ score, size = 'md' }: DriverScoreBadgeProps) {
  if (!score) {
    return (
      <div className={clsx('score-badge', `score-badge--${size}`)} style={{ '--score-color': 'var(--text-muted)' } as React.CSSProperties}>
        <span className="score-badge__value">—</span>
        <div className="score-badge__meta">
          <span className="score-badge__level" style={{ color: 'var(--text-muted)' }}>Pendiente</span>
        </div>
      </div>
    );
  }

  const color = RISK_COLORS[score.level];
  const TrendIcon = score.trend === 'up' ? TrendingUp : score.trend === 'down' ? TrendingDown : Minus;

  return (
    <div className={clsx('score-badge', `score-badge--${size}`)} style={{ '--score-color': color } as React.CSSProperties}>
      <span className="score-badge__value">{formatScore(score.value)}</span>
      <div className="score-badge__meta">
        <span className="score-badge__level" style={{ color }}>{RISK_LABELS[score.level]}</span>
        <TrendIcon size={12} style={{ color }} />
      </div>
    </div>
  );
}

type SortKey = 'score' | 'name' | 'criticalEvents' | 'unit' | 'risk';
type SortDir = 'asc' | 'desc';

interface RankingTableProps {
  drivers: Driver[];
  onDriverClick?: (driver: Driver) => void;
}

export function RankingTable({ drivers, onDriverClick }: RankingTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const riskOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  // Sin score (PROVISIONAL, ver DriverScoreBadge) no hay con qué ordenar —
  // esos conductores caen al final y entre ellos se ordena por nombre.
  const riskRank = (d: Driver) => d.score ? riskOrder[d.score.level] : 3;

  const sorted = [...drivers].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case 'score':
        cmp = (a.score?.value ?? -1) - (b.score?.value ?? -1);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
        break;
      case 'name':
        cmp = a.name.localeCompare(b.name);
        break;
      case 'criticalEvents':
        cmp = (a.criticalEvents ?? -1) - (b.criticalEvents ?? -1);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
        break;
      case 'unit':
        cmp = a.subUnitName.localeCompare(b.subUnitName);
        break;
      case 'risk':
        cmp = riskRank(a) - riskRank(b);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
        break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={13} style={{ opacity: 0.4 }} />;
    return sortDir === 'asc' ? <ChevronUp size={13} style={{ color: 'var(--accent)' }} /> : <ChevronDown size={13} style={{ color: 'var(--accent)' }} />;
  };

  const ColHeader = ({ col, children }: { col: SortKey; children: React.ReactNode }) => (
    <button
      className="ranking-table__sort-btn"
      onClick={() => handleSort(col)}
      aria-label={`Ordenar por ${col}`}
      title={`Ordenar por ${col}`}
    >
      {children}
      <SortIcon col={col} />
    </button>
  );

  return (
    <div className="ranking-table" role="table" aria-label="Ranking de conductores">
      <div className="ranking-table__header" role="row">
        <span role="columnheader">#</span>
        <span role="columnheader"><ColHeader col="name">Conductor</ColHeader></span>
        <span role="columnheader"><ColHeader col="score">Score</ColHeader></span>
        <span role="columnheader"><ColHeader col="risk">Riesgo</ColHeader></span>
        <span role="columnheader"><ColHeader col="criticalEvents">Eventos críticos</ColHeader></span>
        <span role="columnheader"><ColHeader col="unit">Unidad</ColHeader></span>
      </div>
      {sorted.map((driver, index) => {
        const color = driver.score ? RISK_COLORS[driver.score.level] : 'var(--text-muted)';
        return (
          <div
            key={driver.id}
            className={clsx('ranking-table__row', onDriverClick && 'ranking-table__row--clickable')}
            role="row"
            onClick={() => onDriverClick?.(driver)}
            tabIndex={onDriverClick ? 0 : undefined}
          >
            <span className="ranking-table__rank" role="cell">
              {index === 0 && driver.score && <span className="ranking-table__rank-fire">🔴</span>}
              {index + 1}
            </span>
            <div className="ranking-table__driver" role="cell">
              <div className="ranking-table__avatar" style={{ background: `${color}22`, color }}>
                {driver.name.charAt(0)}
              </div>
              <div>
                <p className="ranking-table__name">{driver.name}</p>
                {driver.assignedVehiclePlate && (
                  <p className="ranking-table__plate">{driver.assignedVehiclePlate}</p>
                )}
              </div>
            </div>
            <span className="ranking-table__score" style={{ color }} role="cell">
              {driver.score ? formatScore(driver.score.value) : '—'}
            </span>
            <span role="cell">
              <span
                className="ranking-table__risk-badge"
                style={{ color, background: `${color}18`, borderColor: `${color}44` }}
              >
                {driver.score ? RISK_LABELS[driver.score.level] : 'Pendiente'}
              </span>
            </span>
            <span className="ranking-table__events" role="cell">{driver.criticalEvents ?? '—'}</span>
            <span className="ranking-table__unit" role="cell">{driver.subUnitName}</span>
          </div>
        );
      })}
    </div>
  );
}
